import React from "react";
import { Button, Card, Empty, Tag, Typography } from "@douyinfe/semi-ui";
import {
  AiCoachCustomerDiagnosis,
  intentionLevelLabel,
  intentionLevelStyle,
} from "../../../api/aiCoach";
import { displayLeadStatus } from "../../../api/ApiClient";
import { radius, sp } from "../styles";

const { Title, Paragraph, Text } = Typography;

interface Props {
  items: AiCoachCustomerDiagnosis[];
}

const CustomerDiagnosisList: React.FC<Props> = ({ items }) => {
  const openDetail = (leadId: number) => {
    window.open(`/#/leadDetailNew?data=${encodeURIComponent(JSON.stringify({id:leadId}))}`, "_blank");
  };
  const openProfile = (leadId: number) => {
    window.open(`/#/leadProfile?id=${leadId}`, "_blank");
  };

  return (
    <Card
      style={{ marginBottom: sp.lg, borderRadius: radius.md }}
      bodyStyle={{ padding: sp.lg }}
    >
      <Title heading={5} style={{ margin: `0 0 ${sp.md}` }}>
        客户针对性诊断 · 促成话术
      </Title>

      {!items || items.length === 0 ? (
        <Empty description="本周无 TOP 客户诊断" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: sp.md }}>
          {items.map((d, i) => (
            <div
              key={d.leadId ?? i}
              style={{
                padding: sp.lg,
                background: "var(--semi-color-bg-1)",
                border: "1px solid var(--semi-color-border)",
                borderRadius: radius.md,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: sp.sm,
                  marginBottom: sp.md,
                  flexWrap: "wrap",
                }}
              >
                <Tag color="violet" size="large" style={{ borderRadius: 4 }}>
                  #{d.leadId}
                </Tag>
                {d.leadSnapshot?.leadName && (
                  <Text strong style={{ fontSize: 15 }}>
                    {d.leadSnapshot.leadName}
                  </Text>
                )}
                {d.leadSnapshot?.intentionLevel && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={intentionLevelStyle(d.leadSnapshot.intentionLevel)}>
                      {d.leadSnapshot.intentionLevel}
                    </span>
                    <Text type="tertiary" size="small">
                      {intentionLevelLabel(d.leadSnapshot.intentionLevel)}
                    </Text>
                  </div>
                )}
                {d.leadSnapshot?.star != null && (
                  <Text type="warning" strong style={{ fontSize: 13 }}>
                    {d.leadSnapshot.star}★
                  </Text>
                )}
                {d.leadSnapshot?.status && (
                  <Tag size="small" color="blue">
                    {displayLeadStatus(d.leadSnapshot.status)}
                  </Tag>
                )}
                {d.leadSnapshot?.score != null && (
                  <Tag size="small" color="teal">
                    评分 {d.leadSnapshot.score}
                  </Tag>
                )}
                <div style={{ flex: 1 }} />
                <Button size="small" onClick={() => openDetail(d.leadId)}>
                  详情
                </Button>
                <Button size="small" onClick={() => openProfile(d.leadId)}>
                  画像
                </Button>
              </div>

              {d.techniqueIssues && d.techniqueIssues.length > 0 && (
                <Block
                  label="技巧问题"
                  emoji="⚠️"
                  bg="var(--semi-color-danger-light-default)"
                  fg="var(--semi-color-danger)"
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: sp.xs }}>
                    {d.techniqueIssues.map((t, idx) => (
                      <Tag key={idx} color="red" size="large" style={{ borderRadius: 4 }}>
                        {t}
                      </Tag>
                    ))}
                  </div>
                </Block>
              )}

              {d.closingTechniques && d.closingTechniques.length > 0 && (
                <Block
                  label="促成技巧"
                  emoji="🎯"
                  bg="var(--semi-color-success-light-default)"
                  fg="var(--semi-color-success)"
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: sp.xs }}>
                    {d.closingTechniques.map((t, idx) => (
                      <Text key={idx} style={{ lineHeight: 1.7 }}>
                        <Text strong>{idx + 1}. </Text>
                        {t}
                      </Text>
                    ))}
                  </div>
                </Block>
              )}

              {d.classicScripts && d.classicScripts.length > 0 && (
                <Block
                  label="经典话术"
                  emoji="💬"
                  bg="var(--semi-color-info-light-default)"
                  fg="var(--semi-color-info)"
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: sp.xs }}>
                    {d.classicScripts.map((s, idx) => (
                      <Paragraph
                        key={idx}
                        style={{
                          margin: 0,
                          paddingLeft: sp.md,
                          borderLeft: "3px solid var(--semi-color-info)",
                          color: "var(--semi-color-text)",
                          lineHeight: 1.7,
                        }}
                      >
                        {s}
                      </Paragraph>
                    ))}
                  </div>
                </Block>
              )}

              {d.nextAction && (
                <Block
                  label="下一步行动"
                  emoji="🚀"
                  bg="var(--semi-color-warning-light-default)"
                  fg="var(--semi-color-warning)"
                >
                  <Text style={{ lineHeight: 1.7 }}>{d.nextAction}</Text>
                </Block>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

interface BlockProps {
  label: string;
  emoji: string;
  bg: string;
  fg: string;
  children?: React.ReactNode;
}

const Block: React.FC<BlockProps> = ({ label, emoji, bg, fg, children }) => (
  <div
    style={{
      marginTop: sp.sm,
      padding: sp.md,
      background: bg,
      borderRadius: radius.sm,
    }}
  >
    <div style={{ marginBottom: sp.xs }}>
      <Text strong style={{ color: fg }}>
        {emoji} {label}
      </Text>
    </div>
    {children}
  </div>
);

export default CustomerDiagnosisList;
